import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { FeatureAnnouncementModel } from '../../model';

const ACKNOWLEDGED_STORAGE_KEY = 'vinlivt.acknowledged-announcements';

@Injectable({ providedIn: 'root' })
export class FeatureAnnouncementService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Pending announcements for the logged in user, newest first. Announcements whose
   * acknowledgement previously failed (and is therefore still stored locally) are
   * filtered out and their acknowledgement is retried, so the user is never shown
   * an announcement they already dismissed.
   */
  public getPendingAnnouncements(): Observable<FeatureAnnouncementModel[]> {
    this.retryStoredAcknowledgements();
    const locallyAcknowledged = this.storedIds();

    return this.http
      .get<FeatureAnnouncementModel[]>(`${environment.apiEndpoint}/account/feature-announcements/pending`)
      .pipe(map((announcements) => announcements.filter((a) => !locallyAcknowledged.includes(a.id))));
  }

  /**
   * Fire-and-forget acknowledgement — closing the modal must never wait for (or fail on)
   * this request. On failure the id is stored locally so getPendingAnnouncements()
   * keeps the announcement hidden and retries the acknowledgement on the next load.
   */
  public acknowledgeAnnouncement(id: number): void {
    this.http.post<void>(`${environment.apiEndpoint}/account/feature-announcements/${id}/acknowledge`, null).subscribe({
      error: () => this.storeId(id),
    });
  }

  private retryStoredAcknowledgements(): void {
    for (const id of this.storedIds()) {
      this.http
        .post<void>(`${environment.apiEndpoint}/account/feature-announcements/${id}/acknowledge`, null)
        .subscribe({
          next: () => this.removeId(id),
          error: (error): void => {
            // 404 = announcement no longer exists (or is not visible anymore) — nothing left to acknowledge
            if (error?.status === 404) this.removeId(id);
          },
        });
    }
  }

  private storedIds(): number[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(ACKNOWLEDGED_STORAGE_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : [];
    } catch {
      return [];
    }
  }

  private storeId(id: number): void {
    try {
      const ids = this.storedIds();
      if (!ids.includes(id)) {
        localStorage.setItem(ACKNOWLEDGED_STORAGE_KEY, JSON.stringify([...ids, id]));
      }
    } catch {
      // storage unavailable — worst case the announcement is shown again next load
    }
  }

  private removeId(id: number): void {
    try {
      localStorage.setItem(ACKNOWLEDGED_STORAGE_KEY, JSON.stringify(this.storedIds().filter((i) => i !== id)));
    } catch {
      // storage unavailable — ignore
    }
  }
}
